import type { IEventService } from '@realitycollective/service-framework';
import { createServiceToken } from '@realitycollective/service-framework';
import type {
  TrainingClassSource,
  TrainingFlowStatus,
  TrainingScenario,
  TrainingStep,
  TrainingStepResult,
} from '../../protocol/trainingChannelMessages';
import type { NormalizedPoint, NormalizedRect } from '../../rendering/types';

/**
 * A class arrival that matched the training flow, with enough geometry to
 * place a world label at the detected box centre. Port of the Unity
 * `TrainingDetectionMatch`.
 */
export interface TrainingDetectionMatch {
  readonly label: string;
  /** The class's effective state at this arrival (registry-resolved, e.g. "present"/"on"). */
  readonly state: string;
  readonly conf: number;
  /** Bbox centre in normalized viewport space (0..1, origin bottom-left after invertY). */
  readonly center: NormalizedPoint;
  readonly rect: NormalizedRect;
  /** Arrival time on the pose clock (ms) - feed to the pose history for capture-pose unprojection. Negative for synthetic responses. */
  readonly arrivalTimeMs: number;
  readonly source: TrainingClassSource;
}

/** True when the match carries placeable geometry. */
export function matchHasWorldPoint(match: TrainingDetectionMatch): boolean {
  return match.source !== 'action' && match.arrivalTimeMs >= 0;
}

/** A step became active - the payload handed to the presentation layer. */
export interface TrainingStepActivation {
  readonly step: TrainingStep;
  /** 0-based index into the scenario queue. */
  readonly stepIndex: number;
  readonly stepCount: number;
  /** The arrival that activated the step; null when activated by `begin()` or a state resync. */
  readonly match: TrainingDetectionMatch | null;
}

export type TrainingStateEventMap = {
  /** A scenario was (re)loaded. */
  scenarioLoaded: TrainingScenario;
  /** A step became active - present its UX. */
  stepActivated: TrainingStepActivation;
  /** The active step's detected class was seen again - refresh the world label. */
  currentClassSighted: TrainingDetectionMatch;
  /** The final step's action fired - the procedure is complete. */
  scenarioCompleted: undefined;
};

/**
 * The training flow as the presentation layer sees it - port of the Unity
 * `ITrainingStateService`. On the web only the SERVER authority exists for
 * now (`RemoteTrainingStateService`); a local state-machine port would
 * implement this same interface.
 */
export interface ITrainingStateService extends IEventService<TrainingStateEventMap> {
  readonly scenario: TrainingScenario | null;
  readonly status: TrainingFlowStatus;
  readonly currentStep: TrainingStep | null;
  /** 0-based active step index, -1 when idle/completed. */
  readonly currentStepIndex: number;
  readonly expectedClass: string;
  readonly expectedState: string;
  /** The detection registry: every class captured this run and its current state. */
  readonly detectedObjects: ReadonlyMap<string, string>;
  readonly discardedCount: number;

  /** Display names of the configured scenario catalog - what the hand menu's picker lists. */
  readonly scenarioCatalog: readonly string[];
  /** Index into the catalog of the running scenario, or -1. */
  readonly activeScenarioIndex: number;

  /** Switch to catalog entry `index`. False (no state change) when out of range or not sendable. */
  switchScenario(index: number): boolean;
  /** Replace the scenario (resets the flow to idle). */
  loadScenario(scenario: TrainingScenario): void;
  /** Start the queue at the entry step. */
  begin(): void;
  /** A training form action completed - feed the result back. */
  completeStep(result: TrainingStepResult): void;
  /** Back to idle, keeping the loaded scenario. */
  resetScenario(): void;
}

export const ITrainingStateService = createServiceToken<ITrainingStateService>('ITrainingStateService');
